import { defaultTo, get } from 'lodash';
import { IAccessoryList, IAuxiliaryCalculationsInput, INextArticle, mapVatPricesForNext } from 'next-common';
import { useState } from 'react';
import { OnChangeValue } from 'react-select';
import { calculateVatForElCars } from '../../../../utils';
import {
  DropDownValues,
  IDiscountFieldProps,
  articlesForDiscount,
  dropDownOptions,
  getValidity,
  validateDiscount,
} from '../MarginCartUtils';
import { ArticleType } from 'sales-types-common';
import { BosDropDownOptions, CartPrices, formatNumberInput, InputType, TMessageBarSeverity } from 'types';
import { IconType, MessageBar, PivotField } from '@repo/ui/components';

interface ICalculateImporterDiscountProps extends IDiscountFieldProps {
  setVatPrices?(vatPrices: CartPrices): void;
  onUpdateArticles?(value: IAccessoryList, customerAdvantage?: boolean, discountArticles?: INextArticle[]): void;
}

export const CalculateImporterDiscount = (props: ICalculateImporterDiscountProps) => {
  const [inputType, setInputType] = useState<InputType>(InputType.INTEGER);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const onCampaignImporterChange = (
    input: string | number,
    dropDownValue?: OnChangeValue<BosDropDownOptions, false>,
    event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ): void => {
    if (event.type === 'blur') {
      return null;
    }

    const numericInput = Number(input);
    const isKr = inputType === InputType.INTEGER;

    setHasChanged(getImporterDiscountValue('latestMargin') !== numericInput);
    props.setIsValid(validateDiscount(numericInput, dropDownValue?.label as DropDownValues, 25));

    const auxillaryInput: IAuxiliaryCalculationsInput = {
      discount: props.activeMargin?.campaignDiscount,
      discountPercentage: props.activeMargin?.campaignDiscountPercentage,
      ...(isKr ? { importerDiscount: numericInput } : { importerPercentage: numericInput }),
    };

    let discountArticles = articlesForDiscount(props.articles, auxillaryInput, props.order);

    const excludeImporterType = isKr ? ArticleType.IMPORTCONTRIBUTION_PERCENTAGE : ArticleType.IMPORTCONTRIBUTION;
    discountArticles = discountArticles.filter((article) => article.articleType !== excludeImporterType);

    if (auxillaryInput?.discountPercentage > 0) {
      discountArticles = discountArticles.filter((article) => article.articleType !== ArticleType.CUSTOMERADVANTAGE);
      discountArticles = discountArticles.filter((article) => article.articleType !== ArticleType.DISCOUNT_AMOUNT);
    } else {
      discountArticles = discountArticles.filter((article) => article.articleType !== ArticleType.DISCOUNT_PERCENTAGE);
    }

    console.log(`Importer support articles for ${isKr ? 'KR' : 'percentage'}`, discountArticles);

    const vatCalculation = calculateVatForElCars(discountArticles, props.carType);

    if (!isKr) {
      props.setIsValid(validateDiscount(numericInput, DropDownValues.PERCENTAGE));
    }

    const updatedActiveMargins = {
      ...get(props, 'activeMargin', []),
      campaignImporter: vatCalculation.calculatedImportContribution,
      campaignImporterPercentage: isKr ? 0 : numericInput,
      marginValue: vatCalculation?.marginAmount,
      marginPercentage: vatCalculation?.marginPercentage,
    };
    props?.setActiveMargin(updatedActiveMargins);
    props.setVatPrices(mapVatPricesForNext(vatCalculation));
    if (typeof props.onUpdateArticles === 'function') {
      props.onUpdateArticles(null, true, discountArticles);
    }
  };

  const getImporterDiscountValue = (marginObject: string = 'activeMargin'): number => {
    if (inputType === InputType.INTEGER) {
      return get(props, `${marginObject}.campaignImporter`, 0);
    }
    return get(props, `${marginObject}.campaignImporterPercentage`, 0);
  };

  const handleOnChangeDropDown = (dropDownValue: OnChangeValue<BosDropDownOptions, false>): void => {
    switch (dropDownValue.label) {
      case DropDownValues.KR:
        setInputType(InputType.INTEGER);
        break;
      case DropDownValues.PERCENTAGE:
        setInputType(InputType.FLOAT);
        break;
    }
  };

  return (
    <div className="flex flex-col w-3/4 items-end">
      <label htmlFor="importerSupport" className="w-full mb-2">
        Importørstøtte
      </label>
      <PivotField
        hideValidationIcon
        dropDownOptions={dropDownOptions}
        onChangeDropDown={handleOnChangeDropDown}
        onChange={(input, drop, formatted, event) => onCampaignImporterChange(input, drop, event)}
        value={`${getImporterDiscountValue()}`}
        disabled={false}
        type={inputType}
        decimals={2}
        {...getValidity(
          hasChanged,
          getImporterDiscountValue() !== 0 ? props.isValid : null,
          props.vehicleConfiguration
        )}
        className="w-fit"
      />
      {defaultTo(props.pimSnapshot?.importerSupportExcludingVat, 0) !== 0 && (
        <MessageBar
          severity={TMessageBarSeverity.INFORMATION}
          className="py-1 items-center gap-1 px-3"
          alertIcon={{ icon: IconType.Info, size: 20, fill: 'var(--color-blue)' }}
          alertIconClassName="mt-0"
        >
          <span className="text-sm/[170%] font-semibold">
            {`Importørstøtte: ${
              formatNumberInput(
                Math.abs(get(props, 'pimSnapshot.importerSupportExcludingVat', 0))?.toString(),
                inputType,
                2
              )?.formatted || 0
            } kr`}
          </span>
        </MessageBar>
      )}
    </div>
  );
};
